import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { Save, ArrowLeft } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

export default function EditCandidate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]); 

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    job_id: "",
    status: "",
  });

  const [resumeFile, setResumeFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        
        // Jobs Load Karein
        const jobsRes = await api.get("/jobs", {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobs(jobsRes.data.data);

        // Candidate Load Karein
        const candRes = await api.get(`/candidates/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const c = candRes.data.data;
        setFormData({
          name: c.name || "",
          email: c.email || "",
          phone: c.phone || "",
          job_id: c.job_id || "",
          status: c.status || "",
        });

      } catch (error) {
        toast.error("Failed to fetch candidate details");
        navigate("/candidates");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      e.target.value = null;
    } else {
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("admin_token");
      const submitData = new FormData();
      
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("job_id", formData.job_id);
      submitData.append("status", formData.status);
      
      if (resumeFile) {
        submitData.append("resume_file", resumeFile);
      }

      // 🚨 File updates ke liye humne Backend mein POST banaya hai API route (id ke sath) 🚨
      await api.post(`/candidates/${id}`, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data" 
        },
      });

      toast.success("Candidate updated successfully!");
      navigate("/candidates");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update candidate.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div className="p-8 text-center">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-2 transition-colors">
          <ArrowLeft size={16} /> Back to Candidates
        </button>
        <PageTitle title="Edit Candidate" subtitle="Update details and hiring status." />
      </div>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input type="text" name="phone" required value={formData.phone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Applied For (Job) *</label>
              <select name="job_id" required value={formData.job_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none bg-white">
                <option value="" disabled>Select a Job</option>
                {jobs.map(job => <option key={job.id} value={job.id}>{job.title}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Update Resume (Optional)</label>
              <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={handleFileChange} className="w-full border border-gray-300 rounded-lg px-4 py-2 outline-none file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 text-gray-500 cursor-pointer" />
              <p className="text-xs text-gray-400 mt-1">Leave blank to keep existing resume.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none bg-white">
                <option value="Applied">Applied</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Interview Scheduled">Interview Scheduled</option>
                <option value="Rejected">Rejected</option>
                <option value="Hired">Hired</option>
              </select>
            </div>

          </div>

          <div className="pt-6 flex justify-end border-t border-gray-100">
            <button disabled={saving} type="submit" className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2.5 px-8 rounded-lg transition-colors">
              <Save size={18} /> {saving ? "Updating..." : "Update Candidate"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}